/*
  BE MINDFUL OF WHAT DEPENDENCIES YOU INCLUDE HERE.

  THE CONFIGURATION HERE WILL BE APP WIDE BUT IMPORTS HERE MAY CAUSE
  THE INITIAL LOAD BUNDLES TO INCREASE IN BOTH COUNT AND/OR SIZE.
  THAT LEADS TO MORE TIME FOR INITIAL LOAD OF THE APPLICATION. IF THE
  FUNCTIONALITY IS ONLY FOR LOGGED IN USERS LOOK AT THE INDIVIDUAL
  MODULE ROUTES TO PROVIDE CONFIGURATION THERE.
*/

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
  DOCUMENT,
} from '@angular/core';

import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
} from '@angular/router';

import {
  authHttpInterceptorFn,
  AuthService,
  provideAuth0,
} from '@auth0/auth0-angular';
import { provideNgIdle } from '@ng-idle/core';
import { AfterAppInit } from '@ngrx-addons/common';
import {
  localStorageStrategy,
  providePersistStore,
  sessionStorageStrategy,
} from '@ngrx-addons/persist-state';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';

import { debounceTime, map } from 'rxjs';

import { environment } from '@/environments/environment';
import {
  initializeAgentLocatorScript,
  initializeUserIdle,
} from './app.initializer';
import { routes } from './app.routes';
import { UserService } from './services';
import { appEffects } from './store/effects';
import { metaReducers, reducers } from './store/reducers';
import { clientFeatureKey } from './store/reducers/client.reducer';
import { dataGridPreferencesFeatureKey } from './store/reducers/data-grid-preferences.reducer';
import { entitlementsFeatureKey } from './store/reducers/entitlements.reducer';
import { loggedInUserFeatureKey } from './store/reducers/logged-in-user.reducer';
import { resellerFeatureKey } from './store/reducers/reseller.reducer';
import { siteMapFeatureKey } from './store/reducers/site-map.reducer';
import { usersFeatureKey } from './store/reducers/users.reducer';
import { FTConnectTitleStategy } from './title.strategy';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideHttpClient(withInterceptors([authHttpInterceptorFn])),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideAuth0(environment.auth0),
    provideNgIdle(),
    provideStore(reducers, { metaReducers }),
    provideRouterStore(),
    providePersistStore<typeof reducers>({
      states: [
        {
          key: 'errors',
          // the package exposes localStorageStrategy and
          // sessionStorageStrategy, optionally you can
          // provide your own implementation or even
          // use localForage for indexed db.
          storage: sessionStorageStrategy,
          // optional options (default values)
          // runGuard: () => typeof window !== 'undefined',
          // source: (state) => state,
          // storageKey: `${storageKeyPrefix}-${key}@store`,
          // migrations: [],
          // skip: 1
        },
        {
          key: clientFeatureKey,
          storage: sessionStorageStrategy,
          source: (state) =>
            state.pipe(
              map((state) => ({ ...state, loading: false })),
              debounceTime(1000),
            ),
        },
        {
          key: dataGridPreferencesFeatureKey,
          storage: localStorageStrategy,
        },
        {
          key: entitlementsFeatureKey,
          storage: localStorageStrategy,
        },
        {
          key: loggedInUserFeatureKey,
          storage: sessionStorageStrategy,
          source: (state) =>
            state.pipe(map((state) => ({ ...state, loading: false }))),
        },
        {
          key: resellerFeatureKey,
          storage: sessionStorageStrategy,

          source: (state) =>
            state.pipe(map((state) => ({ ...state, loading: false }))),
        },
        {
          key: siteMapFeatureKey,
          storage: sessionStorageStrategy,

          source: (state) =>
            state.pipe(map((state) => ({ ...state, loading: false }))),
        },
        { key: usersFeatureKey, storage: sessionStorageStrategy },
        // next states to persist, same reducer key can be
        // specified multiple times to save parts of the state
        // to different storages
      ],
      // optional root options (for all, also feature states)
      // storageKeyPrefix: 'some-prefix',
      // optional rehydration strategy
      strategy: AfterAppInit, // or AfterAppInit
    }),
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'USD',
    },
    { provide: TitleStrategy, useClass: FTConnectTitleStategy },
    environment.imports,
    provideEffects(appEffects),
    provideAppInitializer(() =>
      initializeUserIdle(inject(AuthService), inject(UserService)),
    ),
    provideAppInitializer(() => initializeAgentLocatorScript(inject(DOCUMENT))),
  ],
};
